import { useState } from "react";
import { motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { accentText, cardClass, inputClass, primaryButtonClass, secondaryButtonClass } from "../components/register/styles";
import { PendingRegistration } from "../components/register/types/registration";

export function VerifyPage({
  pending,
  onVerify,
  onResend,
}: {
  pending: PendingRegistration | null;
  onVerify: (code: string) => Promise<boolean>;
  onResend: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  if (!pending) return <Navigate to="/register" replace />;

  const handleVerify = async () => {
    setLoading(true);
    setError("");
    const ok = await onVerify(code);
    setLoading(false);
    if (!ok) setError(t("invalidCode"));
  };

  const handleResend = async () => {
    setResendLoading(true);
    await onResend();
    setResendLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[var(--color-grey)] via-[var(--color-white)] to-[var(--color-grey)] p-4 dark:from-[var(--color-darkmode)] dark:via-[var(--color-deepSlate)] dark:to-[var(--color-darkmode)]">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
        className={`max-w-md ${cardClass}`}
      >
        <div className="mb-4 inline-flex items-center gap-2.5 rounded-full bg-[var(--color-tealGreen)]/10 px-4 py-1.5 text-xs font-bold dark:bg-[var(--color-primary)]/15">
          <Mail size={14} className={accentText} />
          <span className={accentText}>{t("verifyTitle")}</span>
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[var(--color-midnight_text)] dark:text-[var(--color-white)]">{t("verifyTitle")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-section)] dark:text-[var(--color-muted)]">{t("verifyDescription", { email: pending.email })}</p>
        {/* {pending.code && (
          <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[var(--color-warning)]">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1l6 12H1L7 1z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" /><path d="M6 5.5v2.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" /><circle cx="6" cy="8.5" r="0.5" fill="currentColor" /></svg>
            {t("codeHint", { code: pending.code })}
          </p>
        )} */}

        <div className="mt-6 space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoalGray)] dark:text-[var(--color-muted)]">{t("code")}</label>
          <input
            value={code}
            onChange={(event) => { setCode(event.target.value); setError(""); }}
            className={inputClass}
            maxLength={6}
            disabled={loading}
          />
          {error && <p className="flex items-center gap-1 text-xs font-medium text-[var(--color-error)]">{error}</p>}
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={handleVerify} disabled={loading} className={primaryButtonClass}>
            {loading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : t("verify")}
          </button>
          <button onClick={handleResend} disabled={resendLoading} className={secondaryButtonClass}>
            {resendLoading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" /> : t("resend")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
